/* eslint-disable max-lines */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchGames } from "../store/slices/gameSlice";
import { getHotGames } from "../store/slices/userActions";
import axios from "axios";
import { getPopupList } from "../store/slices/user";
import CheckinExperience from "../components/CheckinExperience";
import ReviewsModal from "../components/ReviewsModal";

// Use API URL from environment
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
let homeDataFetched = false;

const Toast = ({ message, type = "warning", show, onClose }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  const getIcon = () => {
    switch (type) {
      case "info":
        return "ℹ️";
      case "warning":
        return "⚠️";
      case "error":
        return "❌";
      default:
        return "⚠️";
    }
  };

  const getColors = () => {
    switch (type) {
      case "info":
        return {
          bg: "#FEF3C7",
          border: "#F59E0B",
          text: "#92400E",
        };
      case "warning":
        return {
          bg: "#FEF3C7",
          border: "#F59E0B",
          text: "#92400E",
        };
      case "error":
        return {
          bg: "#FEE2E2",
          border: "#EF4444",
          text: "#991B1B",
        };
      default:
        return {
          bg: "#FEF3C7",
          border: "#F59E0B",
          text: "#92400E",
        };
    }
  };

  const colors = getColors();

  return (
    <div
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        backgroundColor: colors.bg,
        border: `2px solid ${colors.border}`,
        borderRadius: "12px",
        padding: "16px 20px",
        maxWidth: "400px",
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
        zIndex: 10000,
        animation: "slideIn 0.3s ease-out",
        display: "flex",
        alignItems: "center",
        gap: "12px",
      }}
    >
      <span style={{ fontSize: "20px" }}>{getIcon()}</span>
      <span style={{ color: colors.text, fontWeight: "500", flex: 1 }}>
        {message}
      </span>
      <button
        onClick={onClose}
        style={{
          background: "none",
          border: "none",
          fontSize: "18px",
          cursor: "pointer",
          color: colors.text,
          padding: "0",
          marginLeft: "8px",
        }}
      >
        ×
      </button>
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

const POPUP_DISPLAY_MODE = {
  ALWAYS: "ALWAYS",
  SCHEDULED: "SCHEDULED",
};

const now = () => new Date();

const isWithinSchedule = (popup) => {
  if (!popup || popup.displayMode !== POPUP_DISPLAY_MODE.SCHEDULED) {
    return true;
  }

  const start = popup.scheduleStart ? new Date(popup.scheduleStart) : null;
  const end = popup.scheduleEnd ? new Date(popup.scheduleEnd) : null;
  const current = now();

  if (start && Number.isNaN(start.getTime())) return false;
  if (end && Number.isNaN(end.getTime())) return false;
  if (start && current < start) return false;
  if (end && current > end) return false;
  return true;
};

const sortPopupQueue = (popupList) => {
  const scheduled = [];
  const always = [];
  popupList.forEach((popup) => {
    if (popup.displayMode === POPUP_DISPLAY_MODE.SCHEDULED) {
      scheduled.push(popup);
    } else {
      always.push(popup);
    }
  });

  scheduled.sort((a, b) => {
    const startA = a.scheduleStart ? new Date(a.scheduleStart).getTime() : 0;
    const startB = b.scheduleStart ? new Date(b.scheduleStart).getTime() : 0;
    return startA - startB;
  });

  always.sort((a, b) => (a.id || 0) - (b.id || 0));

  return [...scheduled, ...always];
};

const Home = () => {
  const [selectedProvider, setSelectedProvider] = useState(0);
  const [isLoadingHotGames, setIsLoadingHotGames] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { gameList, loading, error } = useSelector((state) => state.games);
  const {
    hotGames: hotGamesData,
    singleUsers,
    allPopup,
  } = useSelector((state) => state.user);

  const [reviewsModalOpen, setReviewsModalOpen] = useState(false);

  // Toast notification state
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "warning",
  });

  // Popup state for post-login banners
  const [openPopup, setOpenPopup] = useState(false);
  const [validPopupImageList, setValidPopupImageList] = useState([]);
  // Use localStorage to track popup shown per user
  const getPopupShownKey = (userId) => `popup_shown_${userId}`;
  const prevUserIdRef = useRef(null);

  const showToast = (message, type = "warning") => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "warning" });
  };

  useEffect(() => {
    if (homeDataFetched) return;
    homeDataFetched = true;
    dispatch(fetchGames());
    dispatch(getHotGames());
  }, [dispatch]);

  const currentUser = useMemo(() => {
    if (!singleUsers) return null;
    if (Array.isArray(singleUsers)) {
      return singleUsers[0] || null;
    }
    if (Array.isArray(singleUsers?.data)) {
      return singleUsers.data[0] || null;
    }
    return singleUsers;
  }, [singleUsers]);

  // Group hot games by provider
  const hotGamesByProvider = React.useMemo(() => {
    if (!hotGamesData || hotGamesData.length === 0) return {};

    return hotGamesData.reduce((acc, game) => {
      if (!acc[game.productId]) {
        acc[game.productId] = [];
      }
      acc[game.productId].push({
        title: game.gameName,
        imageUrl: game.imageUrl,
        userCount: game.onlineUsers,
        rtp: game.rtpPercent,
        gameCode: game.gameCode,
      });
      return acc;
    }, {});
  }, [hotGamesData]);

  const providerLogoMap = useMemo(() => {
    const groups = [
      ...(Array.isArray(gameList?.slots) ? gameList.slots : []),
      ...(Array.isArray(gameList?.liveCasino) ? gameList.liveCasino : []),
      ...(Array.isArray(gameList?.cardGames) ? gameList.cardGames : []),
      ...(Array.isArray(gameList?.sportbook) ? gameList.sportbook : []),
    ];
    return groups.reduce((acc, provider) => {
      const logo = provider?.logo || provider?.image;
      if (!logo) return acc;

      [
        provider?.game,
        provider?.productId,
        provider?.name,
        provider?.productName,
      ]
        .filter(
          (value) => value !== undefined && value !== null && value !== "",
        )
        .forEach((value) => {
          const key = String(value).trim().toLowerCase();
          if (key && !acc[key]) {
            acc[key] = logo;
          }
        });

      return acc;
    }, {});
  }, [gameList]);

  // Get unique providers from hot games, using backend fields
  const hotProviders = React.useMemo(() => {
    if (!hotGamesData || hotGamesData.length === 0) return [];

    // Use productId, productName, logo from backend
    const uniqueProviders = [];
    const seen = new Set();
    hotGamesData.forEach((game) => {
      if (!seen.has(game.productId)) {
        seen.add(game.productId);
        uniqueProviders.push({
          id: uniqueProviders.length,
          name: game.productId,
          displayName: game.productName || game.productId,
          logo:
            providerLogoMap[String(game.productId || "").toLowerCase()] ||
            game.imageUrl ||
            `https://space.mil-168.com/${game.productId}-menu.webp`,
        });
      }
    });
    return uniqueProviders;
  }, [hotGamesData, providerLogoMap]);

  // Use hot games data if available, otherwise fallback to static data
  const providers = React.useMemo(() => {
    const fallbackProviders = [
      { id: 0, name: "PG", displayName: "PG", logo: "pg-cartoon-menu.png" },
      { id: 1, name: "AMBPOKER", logo: "ambpoker-menu-cartoon.png" },
      { id: 2, name: "AMBSLOT", logo: "ambslot-menu-cartoon.png" },
      { id: 3, name: "ASKMEBET", logo: "askmebet-menu-cartoon.png" },
      { id: 4, name: "HACKJSAW", logo: "hacksaw-menu-cartoon.png" },
      { id: 5, name: "PRETTY", logo: "pretty-menu-cartoon.png" },
      { id: 6, name: "PP", logo: "pp-menu-cartoon.png" },
      { id: 7, name: "AMBGAMING", logo: "ambgaming-menu-cartoon.png" },
      { id: 8, name: "SEXY", logo: "sexyslot-menu-cartoon.png" },
    ];
    const source = hotProviders.length > 0 ? hotProviders : fallbackProviders;
    return source.map((provider) => ({
      ...provider,
      logo:
        provider?.logo ||
        providerLogoMap[
          String(provider?.name || provider?.productId || "").toLowerCase()
        ] ||
        `https://space.mil-168.com/menu-image/${provider?.name}.webp`,
    }));
  }, [hotProviders, providerLogoMap]);

  const games = React.useMemo(() => {
    return hotGamesByProvider[providers[selectedProvider]?.name] || [];
  }, [hotGamesByProvider, providers, selectedProvider]);

  // Add random variation to user count for each game
  const gamesWithRandomUsers = React.useMemo(() => {
    return games.map((game) => ({
      ...game,
      displayUserCount: Math.max(
        0,
        game.userCount + Math.floor(Math.random() * 11) - 5,
      ), // Random ±5
    }));
  }, [games]);

  // Prepare popup lists when allPopup changes
  const popupData = useMemo(() => {
    return (allPopup || []).filter(
      (popup) => popup.popupStatus === "NORMAL" && isWithinSchedule(popup),
    );
  }, [allPopup]);

  const popupQueue = useMemo(() => sortPopupQueue(popupData), [popupData]);

  const popupImageList = useMemo(() => {
    return popupQueue.map((item) => ({
      id: item.id,
      mode: item.displayMode,
      src: `${API_BASE_URL}${item?.imageUrl}`,
    }));
  }, [popupQueue]);

  useEffect(() => {
    setValidPopupImageList(popupImageList);
  }, [popupImageList]);

  // When user logs in, fetch popup list and show popup only once per login
  useEffect(() => {
    const prevId = prevUserIdRef.current;

    if (currentUser) {
      const popupKey = getPopupShownKey(currentUser.id);
      const hasShown = localStorage.getItem(popupKey) === "true";

      if (!hasShown) {
        dispatch(getPopupList());
        setTimeout(() => {
          setOpenPopup(true);
        }, 1500);
      } else {
        setOpenPopup(false);
      }
    } else {
      if (prevId) {
        try {
          localStorage.removeItem(getPopupShownKey(prevId));
        } catch (_) {}
      }
      setOpenPopup(false);
    }

    prevUserIdRef.current = currentUser?.id || null;
  }, [currentUser, dispatch]);

  const handleClosePopup = useCallback(() => {
    if (!validPopupImageList.length) {
      setOpenPopup(false);
      return;
    }

    const [, ...next] = validPopupImageList;
    setValidPopupImageList(next);
    const shouldClose = next.length === 0;
    setOpenPopup(!shouldClose);

    if (shouldClose && currentUser) {
      const popupKey = getPopupShownKey(currentUser.id);
      try {
        localStorage.setItem(popupKey, "true");
      } catch (_) {}
    }
  }, [currentUser, validPopupImageList]);

  const providerAllowanceRaw =
    currentUser?.gameProviderAvailable || "ALLPROVIDER";
  const providerAllowanceList =
    providerAllowanceRaw === "ALLPROVIDER"
      ? null
      : providerAllowanceRaw.split(",").map((s) => s.trim().toLowerCase());

  const isProviderAllowed = useCallback(
    (prov) => {
      if (!currentUser) return true;
      if (providerAllowanceList === null) return true;

      const candidates = [];
      if (!prov) return false;
      if (typeof prov === "string") {
        candidates.push(prov);
      } else {
        candidates.push(prov.productId, prov.productName, prov.name, prov.id);
      }

      const normalized = candidates
        .filter((c) => c !== undefined && c !== null && c !== "")
        .map((c) => String(c).toLowerCase());

      return normalized.some((n) => providerAllowanceList?.includes(n));
    },
    [currentUser, providerAllowanceList],
  );

  const allowedProviders = React.useMemo(() => {
    if (!currentUser) return providers;
    return providers.filter((prov) => isProviderAllowed(prov));
  }, [currentUser, providers, isProviderAllowed]);

  const visibleProviders = currentUser ? allowedProviders : providers;

  useEffect(() => {
    if (!allowedProviders.length) return;
    const current = providers.find((p) => p.id === selectedProvider);
    if (!current || (currentUser && !isProviderAllowed(current))) {
      setSelectedProvider(allowedProviders[0].id);
    }
  }, [
    allowedProviders,
    currentUser,
    providers,
    selectedProvider,
    isProviderAllowed,
  ]);

  const startGame = async ({ provider, gamecode }) => {
    if (!provider || !gamecode) return;

    let userId = null;
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        userId = parsed?.id || parsed?.user?.id || parsed?.UserID || null;
      }
    } catch (_) {}

    const data = { id: userId, provider, gamecode };
    const gameWindow = window.open("", "_blank");

    try {
      const res = await axios({
        method: "POST",
        url: `${API_BASE_URL}/gamelogin/gamestart`,
        data: { data },
        maxBodyLength: Infinity,
      });
      if (res.data) {
        if (gameWindow) {
          gameWindow.location.href = res.data;
        } else {
          window.location.href = res.data;
        }
      }
    } catch (error) {
      console.error(error);
      if (gameWindow) gameWindow.close();
      showToast("เกิดข้อผิดพลาดในการเข้าสู่เกมส์", "error");
    }
  };

  const handleTabChangeWithCheck = (id) => {
    const sourceList = currentUser ? allowedProviders : providers;
    const prov =
      sourceList.find((p) => p.id === id) || providers.find((p) => p.id === id);
    if (!prov) {
      setSelectedProvider(id);
      return;
    }

    if (!currentUser) {
      setIsLoadingHotGames(true);
      setSelectedProvider(id);
      // Simulate loading delay
      setTimeout(() => {
        setIsLoadingHotGames(false);
      }, 300);
      return;
    }

    if (isProviderAllowed(prov)) {
      setIsLoadingHotGames(true);
      setSelectedProvider(id);
      // Simulate loading delay
      setTimeout(() => {
        setIsLoadingHotGames(false);
      }, 300);
    } else {
      showToast(
        "ท่านสามารถเล่นได้บางประเภท,บางค่ายเกมส์เท่านั้น เนื่องจากติดเงื่อนไข",
        "warning",
      );
    }
  };

  // add type allowance parsing and provider click handler for slot/casino
  const typeAllowanceList = currentUser?.gameTypeAvailable
    ? currentUser.gameTypeAvailable
        .split(",")
        .map((s) => s.trim().toLowerCase())
    : ["allgametype"];

  const isTypeAllowedFor = (type) => {
    const map = {
      slot: "slots",
      casino: "livecasino",
      fishing: "fishing",
      sport: "sportbook",
    };
    const key = map[type] || type;
    return (
      typeAllowanceList.includes("allgametype") ||
      typeAllowanceList.includes(key) ||
      typeAllowanceList.includes(type)
    );
  };

  const remoteProviderPrefixes = [
    "bs",
    "ng",
    "aws",
    "swg",
    "km",
    "fc",
    "mg",
    "gamatron",
    "ep",
    "pp",
    "netent",
    "joker",
    "bpg",
    "bng",
    "hab",
    "kgl",
    "rlx",
    "ygg",
    "red",
    "qs",
    "ids",
    "tk",
    "ds",
    "nlc",
    "ga",
    "kg",
    "png",
    "pug",
    "fng",
    "nge",
    "1x2",
    "hak",
    "waz",
    "elk",
    "prs",
    "jl",
    "sn",
    "dgs",
  ];

  const handleProviderClick = async (provider, type = "slot") => {
    if (!currentUser) {
      try {
        window.dispatchEvent(new CustomEvent("open-login-modal"));
      } catch (e) {
        // ignore
      }
      showToast("กรุณาเข้าสู่ระบบก่อนเข้าเล่น", "info");
      return;
    }

    if (!isTypeAllowedFor(type)) {
      let msg = "ท่านไม่สามารถเล่นประเภทเกมส์นี้ได้ เนื่องจากติดเงื่อนไข";
      if (type === "slot")
        msg = "ท่านไม่สามารถเล่นประเภทเกมส์สล็อตนี้ได้ เนื่องจากติดเงื่อนไข";
      else if (type === "casino")
        msg = "ท่านไม่สามารถเล่นประเภทเกมส์คาสิโนนี้ได้ เนื่องจากติดเงื่อนไข";
      else if (type === "fishing")
        msg = "ท่านไม่สามารถเล่นประเภทเกมส์ยิงปลาได้ เนื่องจากติดเงื่อนไข";
      else if (type === "sport")
        msg = "ท่านไม่สามารถเล่นประเภทเกมส์กีฬาได้ เนื่องจากติดเงื่อนไข";
      showToast(msg, "warning");
      return;
    }

    if (!isProviderAllowed(provider)) {
      showToast(
        "ท่านสามารถเล่นได้บางประเภท,บางค่ายเกมส์เท่านั้น เนื่องจากติดเงื่อนไข",
        "warning",
      );
      return;
    }

    const providerIdentifier =
      provider?.productId ||
      provider?.game ||
      provider?.productName ||
      provider?.name;
    const providerCode = (providerIdentifier || "").toLowerCase();
    const providerName = (
      provider?.name ||
      provider?.productName ||
      ""
    ).toLowerCase();
    const shouldStartDirect =
      !remoteProviderPrefixes.includes(providerCode) &&
      !remoteProviderPrefixes.includes(providerName);

    if (!shouldStartDirect) {
      const targetPath = type === "casino" ? "/livecasino" : "/slot";
      navigate(
        `${targetPath}?provider=${encodeURIComponent(providerIdentifier)}`,
      );
      return;
    }

    await startGame({
      provider: providerIdentifier,
      gamecode: providerIdentifier,
    });
  };

  const handleHotGameClick = async (game) => {
    if (!currentUser) {
      showToast("กรุณาเข้าสู่ระบบก่อนเข้าเล่น", "info");
      return;
    }

    if (!isTypeAllowedFor("slot")) {
      showToast(
        "ท่านไม่สามารถเล่นประเภทเกมส์สล็อตนี้ได้ เนื่องจากติดเงื่อนไข",
        "warning",
      );
      return;
    }

    const currentProviderName = providers[selectedProvider]?.name;
    if (!isProviderAllowed({ name: currentProviderName })) {
      showToast(
        "ท่านสามารถเล่นได้บางประเภท,บางค่ายเกมส์เท่านั้น เนื่องจากติดเงื่อนไข",
        "warning",
      );
      return;
    }

    await startGame({
      provider: currentProviderName,
      gamecode: game.gameCode,
    });
  };

  const shouldShowHotSection =
    hotProviders.length > 0 && allowedProviders.length > 0;

  const mergedPopularProviders = useMemo(() => {
    const slots = (gameList?.slots || []).slice(0, 9).map((provider) => ({
      ...provider,
      __type: "slot",
    }));
    const casinos = (gameList?.liveCasino || [])
      .slice(0, 9)
      .map((provider) => ({
        ...provider,
        __type: "casino",
      }));

    const seen = new Set();
    return [...slots, ...casinos].filter((provider) => {
      const key = `${provider?.__type}-${provider?.productId || provider?.game || provider?.name || ""}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [gameList?.liveCasino, gameList?.slots]);

  return (
    <div
      style={{
        padding: "16px 0 40px",
        minHeight: "100vh",
        background: "transparent",
      }}
    >
      <CheckinExperience currentUser={currentUser} showToast={showToast} />

      {/* เกมส์มาแรง section only if hotProviders has data */}
      {shouldShowHotSection && (
        <div
          style={{
            margin: "0 auto 18px auto",
            width: "min(980px, calc(100% - 28px))",
            padding: "20px 14px",
            border: "1px solid var(--primaryColor)",
            borderRadius: "22px",
            background:
              "linear-gradient(165deg, color-mix(in srgb, var(--primaryColor) 18%, rgba(10,10,14,0.94)) 0%, color-mix(in srgb, var(--primaryColor) 10%, rgba(10,10,14,0.9)) 48%, rgba(10,10,14,0.94) 100%)",
            backdropFilter: "blur(16px) saturate(120%)",
            boxShadow:
              "0 16px 40px rgba(0,0,0,0.55), 0 0 48px color-mix(in srgb, var(--primaryColor) 22%, transparent)",
          }}
        >
          <h1
            style={{
              color: "#fff",
              textAlign: "center",
              fontSize: "28px",
              fontWeight: "800",
              margin: "12px 0 20px 0",
              textShadow:
                "0 6px 22px color-mix(in srgb, var(--primaryColor) 35%, transparent)",
            }}
          >
            <Icon icon="noto:fire" className="header-fire" />
            <Icon icon="noto:fire" className="header-fire hide-mobile" />
            <Icon icon="noto:fire" className="header-fire" /> เกมส์มาแรง{" "}
            <Icon icon="noto:fire" className="header-fire" />
            <Icon icon="noto:fire" className="header-fire hide-mobile" />
            <Icon icon="noto:fire" className="header-fire" />
          </h1>
          <style>
            {`
              @media (max-width: 768px) {
                div[style*="padding: 32px"] {
                  padding: 24px !important;
                }
                
                h1 {
                  font-size: 24px; /* Smaller size for mobile */
                }
                h1 .iconify {
                  font-size: 24px; /* Adjust icon size for mobile */
                }
                .hide-mobile { display: none !important; }
              }
                 @media (min-width: 768px) {
                h1 {
                  font-size: 32px; /* Smaller size for mobile */
                }
                h1 .iconify {
                  font-size: 32px; /* Adjust icon size for mobile */
                }
              }
            `}
          </style>

          {/* Tabs: playable providers first, then all providers */}
          <div
            style={{
              marginTop: "16px",
              background: "transparent",
              padding: "12px 0",
              borderRadius: "12px",
            }}
          >
            <div
              className="provider-tabs"
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              {visibleProviders.map((prov) => (
                <img
                  key={prov.id}
                  src={prov.logo}
                  alt={prov.displayName || prov.name}
                  onClick={() => handleTabChangeWithCheck(prov.id)}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    if (
                      e.currentTarget.src !==
                      `https://space.mil-168.com/menu-image/${prov.name}.webp`
                    ) {
                      e.currentTarget.src = `https://space.mil-168.com/menu-image/${prov.name}.webp`;
                      return;
                    }
                    if (e.currentTarget.src.endsWith(".webp")) {
                      e.currentTarget.src = `https://space.mil-168.com/menu-image/${prov.name}.gif`;
                    }
                  }}
                  className="provider-logo"
                  style={{
                    width: "104px",
                    height: "104px",
                    cursor: "pointer",
                    opacity:
                      selectedProvider === prov.id
                        ? 1
                        : currentUser
                          ? 0.3
                          : 0.4,
                  }}
                />
              ))}
            </div>
          </div>

          {/* Game Grid */}
          <div
            className="game-grid"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
              justifyContent: "center",
              marginTop: "24px",
              minHeight: "200px",
              padding: "16px",
              background:
                "linear-gradient(145deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
              borderRadius: "16px",
              border:
                "1px solid color-mix(in srgb, var(--primaryColor) 35%, rgba(255,255,255,0.2))",
            }}
          >
            {isLoadingHotGames ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  padding: "40px",
                  color: "#aaa",
                  fontSize: "16px",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      border: "4px solid rgba(255, 255, 255, 0.1)",
                      borderTop: "4px solid var(--primaryColor)",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      margin: "0 auto 16px",
                    }}
                  />
                  <div>กำลังโหลดเกม...</div>
                </div>
              </div>
            ) : gamesWithRandomUsers.length > 0 ? (
              gamesWithRandomUsers.map((game, idx) => (
                <div
                  key={idx}
                  className="game-card"
                  onClick={() => handleHotGameClick(game)}
                  style={{
                    width: "180px",
                    position: "relative",
                    borderRadius: "12px",
                    overflow: "hidden",
                    background: "#ffffff",
                    border:
                      "2px solid color-mix(in srgb, var(--primaryColor) 30%, transparent)",
                    boxShadow:
                      "0 4px 12px color-mix(in srgb, var(--primaryColor) 25%, transparent)",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 20px color-mix(in srgb, var(--primaryColor) 35%, transparent)";
                    e.currentTarget.style.borderColor = "var(--primaryColor)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px color-mix(in srgb, var(--primaryColor) 25%, transparent)";
                    e.currentTarget.style.borderColor =
                      "color-mix(in srgb, var(--primaryColor) 30%, transparent)";
                  }}
                >
                  <div style={{ position: "relative", overflow: "hidden" }}>
                    <img
                      src={game.imageUrl}
                      alt={game.title}
                      style={{
                        width: "100%",
                        display: "block",
                        transition: "transform 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.05)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    />
                    <span
                      className="game-card-badge user-count-badge"
                      style={{
                        position: "absolute",
                        top: "8px",
                        left: "8px",
                        background:
                          "linear-gradient(135deg, rgba(255,87,34,0.95) 0%, rgba(244,67,54,0.95) 100%)",
                        color: "#fff",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                      }}
                    >
                      <Icon
                        icon="fluent-color:people-team-16"
                        style={{
                          animation: "blink 1s infinite",
                          fontSize: "14px",
                        }}
                      />
                      {game.displayUserCount}
                    </span>
                    <span
                      className="game-card-badge rtp-badge"
                      style={{
                        position: "absolute",
                        top: "8px",
                        right: "8px",
                        background:
                          "linear-gradient(135deg, rgba(76,175,80,0.95) 0%, rgba(56,142,60,0.95) 100%)",
                        color: "#fff",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: "600",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                      }}
                    >
                      RTP {game.rtp}%
                    </span>
                  </div>
                  <div
                    style={{
                      padding: "12px 8px",
                      textAlign: "center",
                      background:
                        "linear-gradient(to bottom, color-mix(in srgb, var(--primaryColor) 12%, white), color-mix(in srgb, var(--primaryColor) 6%, white))",
                    }}
                  >
                    <div className="game-card-info">
                      <span
                        style={{
                          background:
                            "linear-gradient(135deg, rgba(255,87,34,0.95) 0%, rgba(244,67,54,0.95) 100%)",
                          color: "#fff",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                        }}
                      >
                        <Icon
                          icon="fluent-color:people-team-16"
                          style={{
                            animation: "blink 1s infinite",
                            fontSize: "14px",
                          }}
                        />
                        {game.displayUserCount}
                      </span>
                      <span
                        style={{
                          background:
                            "linear-gradient(135deg, rgba(76,175,80,0.95) 0%, rgba(56,142,60,0.95) 100%)",
                          color: "#fff",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: "600",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                        }}
                      >
                        RTP {game.rtp}%
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "800",
                        color:
                          "color-mix(in srgb, var(--primaryColor) 85%, black)",
                        lineHeight: "1.3",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        minHeight: "34px",
                        textShadow: "none",
                      }}
                      title={game.title}
                    >
                      {game.title}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  padding: "40px",
                  color: "#0e6c5e",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                ไม่มีเกมในค่ายนี้
              </div>
            )}
          </div>
        </div>
      )}

      {/* Combined Popular Games Section */}
      <div
        style={{
          margin: "0 auto 18px auto",
          width: "min(980px, calc(100% - 28px))",
          padding: "20px 14px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          border: "1px solid var(--primaryColor)",
          borderRadius: "22px",
          background:
            "linear-gradient(165deg, color-mix(in srgb, var(--primaryColor) 16%, rgba(10,10,14,0.94)) 0%, color-mix(in srgb, var(--primaryColor) 9%, rgba(10,10,14,0.9)) 52%, rgba(10,10,14,0.94) 100%)",
          backdropFilter: "blur(16px) saturate(120%)",
          boxShadow:
            "0 16px 40px rgba(0,0,0,0.55), 0 0 48px color-mix(in srgb, var(--primaryColor) 20%, transparent)",
        }}
      >
        <div
          className="popular-header"
          style={{ position: "relative", overflow: "hidden" }}
        >
          {/* Animated ambient glow */}
          <div
            style={{
              position: "absolute",
              top: "-50%",
              left: "50%",
              transform: "translateX(-50%)",
              width: 280,
              height: 160,
              borderRadius: "50%",
              background: "var(--primaryColor)",
              filter: "blur(72px)",
              opacity: 0.16,
              pointerEvents: "none",
              animation: "popHeaderPulse 4s ease-in-out infinite",
            }}
          />

          {/* Main row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              position: "relative",
              zIndex: 1,
              padding: "6px 4px",
            }}
          >
            {/* Icon badge */}
            <div
              style={{
                width: 50,
                height: 50,
                borderRadius: 14,
                flexShrink: 0,
                background:
                  "linear-gradient(145deg, var(--primaryColor), color-mix(in srgb, var(--primaryColor) 60%, #000))",
                boxShadow:
                  "0 6px 20px color-mix(in srgb, var(--primaryColor) 40%, transparent)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Icon
                icon="fluent-color:star-48"
                width={30}
                height={30}
                style={{ position: "relative", zIndex: 1 }}
              />
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: 14,
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, transparent 55%)",
                  pointerEvents: "none",
                }}
              />
            </div>

            {/* Title + subtitle */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: 22,
                  fontWeight: 900,
                  color: "#fff",
                  letterSpacing: "0.01em",
                  lineHeight: 1.25,
                  textShadow:
                    "0 4px 18px color-mix(in srgb, var(--primaryColor) 30%, transparent)",
                }}
              >
                ยอดฮิต
              </h2>
            </div>

            {/* Right side: count chip */}
            <div
              style={{
                padding: "5px 12px",
                borderRadius: 999,
                flexShrink: 0,
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "rgba(255,255,255,0.7)",
                fontSize: 12,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <Icon
                icon="solar:gamepad-bold-duotone"
                width={15}
                height={15}
                style={{ opacity: 0.7 }}
              />
              {mergedPopularProviders.length} ค่าย
            </div>
          </div>

          {/* Bottom divider */}
          <div
            style={{
              marginTop: 12,
              height: 1,
              background:
                "linear-gradient(90deg, transparent 0%, color-mix(in srgb, var(--primaryColor) 45%, transparent) 50%, transparent 100%)",
            }}
          />

          <style>{`
            @keyframes popHeaderPulse {
              0%, 100% { opacity: 0.14; transform: translateX(-50%) scale(1); }
              50% { opacity: 0.22; transform: translateX(-50%) scale(1.15); }
            }
          `}</style>
        </div>

        {loading && (
          <p style={{ color: "#fff", textAlign: "center", margin: "8px 0" }}>
            Loading games...
          </p>
        )}
        {error && (
          <p style={{ color: "#ffb4b4", textAlign: "center", margin: "8px 0" }}>
            Error: {error}
          </p>
        )}

        <div
          className="popular-provider-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
            gap: 14,
          }}
        >
          {mergedPopularProviders.map((provider, index) => {
            const img = `https://space.mil-168.com/menu-image/${provider?.game}.webp`;
            const src = img.startsWith("http")
              ? img
              : `https://space.mil-168.com/${img}`;

            return (
              <div
                key={`${provider.__type}-${provider.productId || provider.game || index}`}
                style={{
                  backgroundColor: "#2d2d2d",
                  borderRadius: 12,
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  border: "1px solid #404040",
                  position: "relative",
                  overflow: "hidden",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.04)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 25px var(--primaryColor,--primaryColor)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "none";
                }}
                onClick={() => handleProviderClick(provider, provider.__type)}
              >
                <img
                  className="popular-provider-cover"
                  src={src}
                  alt={provider.productName || provider.name}
                  style={{
                    width: "100%",
                    objectFit: "contain",
                    borderRadius: 8,
                    minHeight: 126,
                  }}
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />

                <span
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    background:
                      provider.__type === "casino"
                        ? "linear-gradient(135deg, rgba(244,67,54,0.95) 0%, rgba(183,28,28,0.95) 100%)"
                        : "linear-gradient(135deg, rgba(33,150,243,0.95) 0%, rgba(13,71,161,0.95) 100%)",
                    color: "#fff",
                    padding: "3px 7px",
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  {provider.__type === "casino" ? "คาสิโน" : "สล็อต"}
                </span>

                <h3
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    color: "white",
                    fontSize: 13,
                    margin: 0,
                    padding: "6px 10px",
                    fontWeight: 700,
                    backgroundColor: "rgba(0,0,0,0.75)",
                    borderRadius: "0 0 8px 8px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {provider.productName || provider.name}
                </h3>
              </div>
            );
          })}
        </div>
        <style>
          {`
            @media (max-width: 991px) {
              .popular-provider-grid {
                grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
              }
              .popular-provider-cover {
                min-height: 108px !important;
              }
            }

            @media (max-width: 600px) {
              .popular-provider-grid {
                grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
              }
              .popular-provider-cover {
                min-height: 92px !important;
              }
            }
          `}
        </style>
      </div>

      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
      />

      {/* Popup Dialog shown after login (custom, no external deps) */}
      {openPopup && validPopupImageList.length > 0 && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1400,
            padding: 20,
          }}
          onClick={handleClosePopup}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              width: "100%",
              maxWidth: 1000,
              maxHeight: "90vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            <button
              aria-label="close"
              onClick={handleClosePopup}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                background: "#e53935",
                color: "#fff",
                border: 0,
                borderRadius: "50%",
                width: 40,
                height: 40,
                cursor: "pointer",
                zIndex: 1500,
                fontSize: 18,
                lineHeight: 1,
              }}
            >
              ✕
            </button>

            <img
              src={validPopupImageList[0]?.src || validPopupImageList[0]}
              alt="popup"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "80vh",
                objectFit: "contain",
                borderRadius: 8,
              }}
              onError={() => setValidPopupImageList((prev) => prev.slice(1))}
            />
            {/* Removed 'Don't show again' checkbox to always rely on per-login flag */}
          </div>
        </div>
      )}

      <style>
        {`
          .game-card-info {
            display: none;
          }
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }


          
          @media (max-width: 768px) {
            div[style*="maxWidth: 920px"] {
              padding: 16px !important;
            }
            .game-card-badge {
              display: none !important;
            }
            .game-card-info {
              display: flex;
              justify-content: center;
              align-items: center;
              gap: 6px;
              margin-bottom: 6px;
            }
            .game-card-info > span {
              padding: 2px 8px !important;
              font-size: 10px !important;
              display: inline-flex !important;
              align-items: center !important;
              gap: 4px !important;
            }
            .game-card-info .iconify {
              font-size: 12px !important;
            }
            .game-grid {
              display: grid !important;
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 12px !important;
              padding: 0 8px;
            }
            
            .game-card {
              width: 100% !important;
            }
            
            .provider-tabs {
              display: grid !important;
              grid-template-columns: repeat(3, 1fr) !important;
              gap: 8px !important;
              padding: 0 8px !important;
              justify-items: center !important;
              align-items: center !important;
            }
            
            .provider-logo {
              width: 80px !important;
              height: 80px !important;
              object-fit: contain !important;
            }
            
            .slots-grid {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 12px !important;
            }
            
            .slots-grid > div:first-child {
              grid-column: span 2 !important;
              grid-row: span 1 !important;
            }
            
            .casino-grid {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 12px !important;
            }
            
            .casino-grid > div:first-child {
              grid-column: span 2 !important;
              grid-row: span 1 !important;
            }
          }
        `}
      </style>
      {/* ── Reviews Menu Section ── */}
      <div
        style={{
          margin: "0 auto 24px auto",
          width: "min(980px, calc(100% - 28px))",
        }}
      >
        <button
          type="button"
          onClick={() => setReviewsModalOpen(true)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 22px",
            background:
              "linear-gradient(135deg, color-mix(in srgb, var(--primaryColor) 22%, rgba(10,10,14,0.92)) 0%, color-mix(in srgb, var(--primaryColor) 12%, rgba(10,10,14,0.9)) 100%)",
            border:
              "1px solid color-mix(in srgb, var(--primaryColor) 45%, transparent)",
            borderRadius: 18,
            cursor: "pointer",
            boxShadow:
              "0 8px 24px rgba(0,0,0,0.45), 0 0 28px color-mix(in srgb, var(--primaryColor) 16%, transparent)",
            transition: "transform 0.18s ease, box-shadow 0.18s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow =
              "0 12px 32px rgba(0,0,0,0.55), 0 0 36px color-mix(in srgb, var(--primaryColor) 24%, transparent)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow =
              "0 8px 24px rgba(0,0,0,0.45), 0 0 28px color-mix(in srgb, var(--primaryColor) 16%, transparent)";
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                background:
                  "linear-gradient(135deg, color-mix(in srgb, var(--primaryColor) 70%, white 30%), var(--primaryColor))",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow:
                  "0 4px 14px color-mix(in srgb, var(--primaryColor) 50%, transparent)",
                flexShrink: 0,
              }}
            >
              <Icon icon="fluent-color:star-emphasis-24" width={26} />
            </div>
            <div style={{ textAlign: "left" }}>
              <div
                style={{
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 16,
                  lineHeight: 1.2,
                }}
              >
                รีวิวจากลูกค้า
              </div>
              <div
                style={{
                  color: "rgba(255,255,255,0.5)",
                  fontSize: 12,
                  marginTop: 2,
                }}
              >
                ดูความคิดเห็นจากผู้ใช้จริง
              </div>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: "rgba(255,255,255,0.5)",
              fontSize: 13,
            }}
          >
            <span>ดูทั้งหมด</span>
            <Icon
              icon="fluent:chevron-right-24-filled"
              width={18}
              style={{ color: "var(--primaryColor)" }}
            />
          </div>
        </button>
      </div>

      <ReviewsModal
        isOpen={reviewsModalOpen}
        onClose={() => setReviewsModalOpen(false)}
      />
    </div>
  );
};

export default Home;
